import api from './api';

export interface Notification {
  id: string;
  type: 'message' | 'application' | 'payment' | 'maintenance' | 'reminder' | 'system';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl?: string;
  actionLabel?: string;
  relatedId?: string;
  metadata?: {
    propertyTitle?: string;
    propertyAddress?: string;
    applicationStatus?: string;
    amount?: number;
    dueDate?: string;
    senderName?: string;
  };
}

export interface NotificationPreferences {
  email: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
    marketing: boolean;
  };
  push: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
  };
  inApp: {
    messages: boolean;
    applications: boolean;
    payments: boolean;
    maintenance: boolean;
    reminders: boolean;
  };
}

export interface NotificationsResponse {
  success: boolean;
  data: {
    notifications: Notification[];
    unreadCount: number;
  };
}

export interface NotificationPreferencesResponse {
  success: boolean;
  data: NotificationPreferences;
}

class NotificationApiService {
  /**
   * Get all notifications for the current user
   */
  async getNotifications(): Promise<NotificationsResponse> {
    try {
      console.log('🔔 Fetching notifications...');
      const response = await api.get('/notifications');
      console.log('✅ Notifications fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notifications as read
   */
  async markAsRead(notificationIds?: string[]): Promise<void> {
    try {
      const payload = notificationIds ? { notificationIds } : { type: 'all' };
      await api.put('/notifications/read', payload);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      await api.delete(`/notifications/${notificationId}`);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<NotificationPreferencesResponse> {
    try {
      const response = await api.get('/notifications/preferences');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: NotificationPreferences): Promise<void> {
    try {
      await api.put('/notifications/preferences', preferences);
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  /**
   * Create a test notification (for development)
   */
  async createTestNotification(data: {
    type?: string;
    title?: string;
    message?: string;
  }): Promise<any> {
    try {
      const response = await api.post('/notifications/test', data);
      return response.data;
    } catch (error) {
      console.error('Error creating test notification:', error);
      throw error;
    }
  }

  /**
   * Trigger saved search check (for development)
   */
  async triggerSavedSearchCheck(): Promise<any> {
    try {
      const response = await api.post('/notifications/trigger-saved-search-check');
      return response.data;
    } catch (error) {
      console.error('Error triggering saved search check:', error);
      throw error;
    }
  }

  /**
   * Trigger rent reminders (for development)
   */
  async triggerRentReminders(): Promise<any> {
    try {
      const response = await api.post('/notifications/trigger-rent-reminders');
      return response.data;
    } catch (error) {
      console.error('Error triggering rent reminders:', error);
      throw error;
    }
  }
}

export const notificationApi = new NotificationApiService(); 